/**
 * Compare management response text to VDR excerpts — surface items in documents
 * but omitted from the management answer (coverage gaps).
 */

const NAME_STOPWORDS = new Set([
    'the',
    'and',
    'for',
    'from',
    'with',
    'your',
    'our',
    'their',
    'this',
    'that',
    'are',
    'was',
    'were',
    'has',
    'have',
    'not',
    'but',
    'can',
    'will',
    'all',
    'any',
    'who',
    'how',
    'what',
    'when',
    'where',
    'which',
    'business',
    'integration',
    'devops',
    'team',
    'staff',
    'help',
    'work',
    'working',
    'developer',
    'developers',
    'microsoft',
    'office',
    'windows',
    'azure',
    'amazon',
    'google',
    'executech',
    'swipeclock',
    'project',
    'admin',
    'medium',
    'high',
    'low',
    'priority',
    'status',
    'none',
    'yes',
    'no',
    'see',
    'also',
    'may',
    'maing',
    'making',
    'sure',
    'voice',
    'ways',
    'ma',
]);

const OMISSIONS_SECTION_SPLIT =
    /(?:^|\n)(?:#{1,3}\s*)?(?:\d+\.\s*)?Management omissions(?:\s+from\s+VDR)?[^\n]*\n/i;

function normalizeForMatch(value) {
    return String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9@.\s-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** @param {string} text */
export function questionExpectsEnumeration(text) {
    const lower = String(text || '').toLowerCase();
    if (/\b(name|list|identify|provide)\b/.test(lower) && /\b(all|every|each)\b/.test(lower)) {
        return true;
    }
    if (/\b(name|list)\b/.test(lower) && /\b(team|member|staff|employee|people|account|user)\b/.test(lower)) {
        return true;
    }
    if (/\bhow many\b/.test(lower) && /\b(staff|employee|people|fte)\b/.test(lower)) {
        return true;
    }
    return false;
}

/**
 * @param {string} text
 * @returns {string[]}
 */
export function extractCandidateProperNames(text) {
    const raw = String(text || '');
    if (!raw.trim()) return [];

    const found = new Map();

    const add = name => {
        const n = String(name || '').trim();
        if (!n || n.length < 2) return;
        const key = normalizeForMatch(n);
        if (!key || NAME_STOPWORDS.has(key)) return;
        if (/^\d+$/.test(key)) return;
        found.set(key, n);
    };

    const commaLists = raw.match(
        /\b[A-Z][a-z]{1,}(?:\s+[A-Z][a-z]{1,})?(?:\s*(?:,|and)\s*[A-Z][a-z]{1,}(?:\s+[A-Z][a-z]{1,})?)+\b/g,
    );
    for (const list of commaLists || []) {
        list.split(/\s*(?:,| and )\s*/).forEach(part => add(part.trim()));
    }

    for (const m of raw.matchAll(/\b[A-Z][a-z]{2,}\b/g)) {
        add(m[0]);
    }

    return [...found.values()];
}

function nameMentionedInManagement(name, managementNorm) {
    const n = normalizeForMatch(name);
    if (!n || !managementNorm) return false;
    if (managementNorm.includes(n)) return true;
    const parts = n.split(' ').filter(p => p.length >= 2);
    if (parts.length > 1) {
        return parts.every(p => managementNorm.includes(p));
    }
    return false;
}

/**
 * @param {string} managementAnswer
 * @param {string[]} vdrNames
 */
export function diffNamesMissingFromManagement(managementAnswer, vdrNames) {
    const mgmtNorm = normalizeForMatch(managementAnswer);
    const missing = [];
    const seen = new Set();

    for (const name of vdrNames) {
        const key = normalizeForMatch(name);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        if (!nameMentionedInManagement(name, mgmtNorm)) {
            missing.push(name);
        }
    }

    return missing.sort((a, b) => a.localeCompare(b));
}

/**
 * @param {{ documentary?: Array<{ citation?: object, linkedChunk?: object }> }} grouped
 */
export function extractNamesFromVdrGroup(grouped) {
    const chunks = [];
    for (const entry of grouped?.documentary || []) {
        const text = entry?.linkedChunk?.chunk || entry?.citation?.text_preview || '';
        if (text) chunks.push(text);
    }
    for (const entry of grouped?.contradictingVdr || []) {
        const text = entry?.linkedChunk?.chunk || entry?.citation?.text_preview || '';
        if (text) chunks.push(text);
    }

    const combined = chunks.join('\n');
    return extractCandidateProperNames(combined);
}

/**
 * @param {string} rawAnswer
 * @returns {string[]}
 */
export function parseManagementOmissionsFromAnswer(rawAnswer) {
    const text = String(rawAnswer || '');
    const match = text.match(OMISSIONS_SECTION_SPLIT);
    if (!match || match.index == null) return [];

    let section = text.slice(match.index + match[0].length).trim();
    const nextHeading = section.search(/\n(?:#{1,3}\s*)?\d+\.\s+/);
    if (nextHeading > 0) section = section.slice(0, nextHeading).trim();

    if (/^none\b/i.test(section) || /none identified/i.test(section)) {
        return [];
    }

    const items = [];
    for (const line of section.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed || !/^[-*]/.test(trimmed)) continue;
        let item = trimmed.replace(/^[-*]\s*/, '');
        item = item.replace(/^missing from management response:\s*/i, '');
        item = item.replace(/\s*\(see\s*\[[\d,\s]+\]\)\s*$/i, '');
        item = item.replace(/\s*—\s*see\s*\[[\d,\s]+\]\s*$/i, '');
        item = item.trim();
        if (item && !/^none\b/i.test(item)) items.push(item);
    }
    return items;
}

/**
 * @param {{
 *   diligenceQuestion?: string,
 *   managementAnswer?: string,
 *   grouped?: object,
 *   rawAnswer?: string,
 *   parsedAnswer?: { managementOmissions?: string[] },
 * }} input
 * @returns {{ text: string, entity: string, source: 'heuristic' | 'ai' }[]}
 */
export function computeManagementOmissionGaps(input = {}) {
    const { diligenceQuestion, managementAnswer, grouped, rawAnswer, parsedAnswer } = input;
    const gaps = [];
    const seen = new Set();

    const push = (entity, source, prefix = 'Missing from management response') => {
        const e = String(entity || '').trim();
        if (!e || seen.has(e.toLowerCase())) return;
        seen.add(e.toLowerCase());
        gaps.push({
            entity: e,
            source,
            text: `${prefix}: ${e} (found in VDR evidence, not stated in management answer)`,
        });
    };

    for (const item of parsedAnswer?.managementOmissions || []) {
        push(item, 'ai');
    }

    for (const item of parseManagementOmissionsFromAnswer(rawAnswer)) {
        push(item, 'ai');
    }

    if (!questionExpectsEnumeration(diligenceQuestion)) {
        return gaps;
    }

    const vdrNames = extractNamesFromVdrGroup(grouped);
    const heuristicMissing = diffNamesMissingFromManagement(managementAnswer, vdrNames);
    for (const name of heuristicMissing) {
        const already = gaps.some(g => normalizeForMatch(g.entity) === normalizeForMatch(name));
        if (!already) push(name, 'heuristic');
    }

    return gaps;
}
